import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Description as DescriptionIcon } from '@mui/icons-material';
import { getPublicWaiver, submitPublicWaiver, type ExistingWaiverSignature, type PublicWaiverResponse } from '../../lib/waiverApi';

function formatSignedDate(value: string | null) {
  if (!value) return 'Unknown date';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export function WaiverPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const theme = useTheme();
  const [waiver, setWaiver] = useState<PublicWaiverResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [checkedEmail, setCheckedEmail] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [existingSignature, setExistingSignature] = useState<ExistingWaiverSignature | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<ExistingWaiverSignature | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    typedSignature: '',
    accepted: false,
  });

  useEffect(() => {
    if (!tenantSlug) {
      setError('Invalid waiver link');
      setLoading(false);
      return;
    }

    const loadWaiver = async () => {
      try {
        setLoading(true);
        const response = await getPublicWaiver(tenantSlug);
        setWaiver(response);
      } catch (requestError: any) {
        console.error('Error loading public waiver:', requestError);
        setError(requestError.message || 'Failed to load waiver');
      } finally {
        setLoading(false);
      }
    };

    loadWaiver();
  }, [tenantSlug]);

  const primaryColor = useMemo(
    () => waiver?.tenant.branding?.primaryColor || theme.palette.primary.main,
    [theme.palette.primary.main, waiver?.tenant.branding?.primaryColor]
  );

  const handleCheckEmail = async () => {
    if (!tenantSlug || !email.trim()) return;

    setCheckingEmail(true);
    setError(null);
    setExistingSignature(null);
    setSubmitSuccess(null);

    try {
      const response = await getPublicWaiver(tenantSlug, email.trim());
      setWaiver(response);
      setCheckedEmail(email.trim());
      setExistingSignature(response.existingSignature);
    } catch (requestError: any) {
      console.error('Error checking waiver status:', requestError);
      setError(requestError.message || 'Failed to check waiver status');
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleSubmit = async () => {
    if (!tenantSlug || !checkedEmail) return;

    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.typedSignature.trim()) {
      setError('Please complete the signature form before submitting');
      return;
    }

    if (!formData.accepted) {
      setError('You must accept the waiver before submitting');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await submitPublicWaiver({
        tenantSlug,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: checkedEmail,
        phone: formData.phone.trim() || undefined,
        typedSignature: formData.typedSignature.trim(),
        accepted: true,
      });

      const successState: ExistingWaiverSignature = {
        signedAt: response.signedAt,
        signatureId: response.signatureId,
        templateId: waiver?.template.id || '',
        signerFirstName: formData.firstName.trim(),
        signerLastName: formData.lastName.trim(),
        signerEmail: checkedEmail,
      };

      setSubmitSuccess(successState);
      setExistingSignature(successState);
    } catch (requestError: any) {
      console.error('Error submitting waiver:', requestError);
      setError(requestError.message || 'Failed to submit waiver');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setCheckedEmail('');
    setExistingSignature(null);
    setSubmitSuccess(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !waiver) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!waiver) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 4 }}>
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 2, sm: 3 },
          mb: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            {waiver.tenant.branding?.logoUrl ? (
              <Box
                component="img"
                src={waiver.tenant.branding.logoUrl}
                alt={waiver.tenant.name}
                sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 1 }}
              />
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 1,
                  bgcolor: primaryColor,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.75rem',
                  fontWeight: 700,
                }}
              >
                {waiver.tenant.name.charAt(0).toUpperCase()}
              </Box>
            )}
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {waiver.tenant.name}
              </Typography>
              <Typography color="text.secondary">Digital waiver</Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl">
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
          <Paper
            sx={{
              flex: 1.4,
              minHeight: { xs: 420, lg: 'calc(100vh - 220px)' },
              overflow: 'hidden',
            }}
          >
            <Box
              component="iframe"
              title={waiver.template.title}
              src={waiver.pdfUrl}
              sx={{
                width: '100%',
                height: '100%',
                border: 0,
              }}
            />
          </Paper>

          <Card sx={{ flex: 1, alignSelf: 'flex-start' }}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DescriptionIcon color="primary" />
                  <Box>
                    <Typography variant="h6">{waiver.template.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Version {waiver.template.version}
                    </Typography>
                  </Box>
                </Stack>

                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {!checkedEmail && (
                  <>
                    <Typography color="text.secondary">
                      Enter your email to check whether this waiver is already on file.
                    </Typography>
                    <TextField
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                    />
                    <Button variant="contained" onClick={handleCheckEmail} disabled={checkingEmail || !email.trim()}>
                      {checkingEmail ? 'Checking...' : 'Continue'}
                    </Button>
                  </>
                )}

                {checkedEmail && existingSignature && (
                  <>
                    <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success">
                      <Typography fontWeight={600}>Waiver already on file</Typography>
                      <Typography variant="body2">
                        Signed on {formatSignedDate(existingSignature.signedAt)} for {existingSignature.signerEmail || checkedEmail}.
                      </Typography>
                    </Alert>
                    <Button variant="outlined" onClick={handleUseDifferentEmail}>
                      Use a different email
                    </Button>
                  </>
                )}

                {checkedEmail && !existingSignature && (
                  <>
                    <TextField label="Email" value={checkedEmail} InputProps={{ readOnly: true }} />
                    <TextField
                      label="First name"
                      value={formData.firstName}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setFormData((current) => ({ ...current, firstName: event.target.value }))
                      }
                    />
                    <TextField
                      label="Last name"
                      value={formData.lastName}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setFormData((current) => ({ ...current, lastName: event.target.value }))
                      }
                    />
                    <TextField
                      label="Phone (optional)"
                      value={formData.phone}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setFormData((current) => ({ ...current, phone: event.target.value }))
                      }
                    />
                    <TextField
                      label="Type your full legal name"
                      value={formData.typedSignature}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setFormData((current) => ({ ...current, typedSignature: event.target.value }))
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.accepted}
                          onChange={(event) =>
                            setFormData((current) => ({ ...current, accepted: event.target.checked }))
                          }
                        />
                      }
                      label="I have read and agree to this waiver."
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button variant="outlined" onClick={handleUseDifferentEmail}>
                        Change email
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                      >
                        {submitting ? 'Submitting...' : submitSuccess ? 'Signed' : 'Sign Waiver'}
                      </Button>
                    </Stack>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
